/**
 * Clears the current text selection in the document.
 */
export const clearDocumentSelection = () => {
  const sel = window.getSelection();
  if (sel) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (sel.removeAllRanges) {
      sel.removeAllRanges();
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (sel.empty) {
      sel.empty();
    }
  }
};

/**
 * The focused element, pierced through shadow roots. document.activeElement
 * only returns the shadow host, so walk down to the real focused element
 * (e.g. the textarea inside a <vscode-textarea>).
 */
export function deepActiveElement(): Element | null {
  let active: Element | null = document.activeElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active;
}

/**
 * True if the element accepts text input (input/textarea/select/contentEditable).
 * Use to skip global keyboard handlers when focus is in a form field.
 *
 * Pierces shadow roots: `document.activeElement` returns the shadow HOST for
 * focus inside a web component (e.g. `<vscode-textarea>`, whose real
 * `<textarea>` lives in shadow DOM), so resolve to the deepest focused element
 * before checking. Without this, global key handlers (j/k, find shortcuts, …)
 * fire while the user is typing into such a control.
 */
export function isEditableTarget(el: Element | null): boolean {
  let active: Element | null = el;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  if (!active) return false;
  const tag = active.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (active instanceof HTMLElement && active.isContentEditable) return true;
  return false;
}
